import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ORG_TYPES = [
  { value: "escola", label: "Escola / rede educacional" },
  { value: "entidade", label: "Entidade de classe / associação" },
  { value: "varejo", label: "Varejista de grande porte" },
  { value: "outro", label: "Outro" },
];

const SIZES = [
  { value: "<500", label: "Menos de 500 pessoas" },
  { value: "500-2k", label: "500 – 2.000" },
  { value: "2k-10k", label: "2.000 – 10.000" },
  { value: "10k-50k", label: "10.000 – 50.000" },
  { value: "50k+", label: "Mais de 50.000" },
];

const INITIAL = {
  name: "",
  role: "",
  organization: "",
  org_type: "",
  email: "",
  phone: "",
  community_size: "",
  main_challenge: "",
  message: "",
};

export default function SaasLeadForm() {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.name || !form.organization || !form.org_type || !form.email) {
      toast.error("Preencha nome, organização, tipo e e-mail.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/saas-leads`, {
        ...form,
        role: form.role || null,
        phone: form.phone || null,
        community_size: form.community_size || null,
        main_challenge: form.main_challenge || null,
        message: form.message || null,
      });
      setDone(true);
      toast.success("Recebemos seu interesse. Em até 5 dias úteis retornamos.");
      setForm(INITIAL);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Não foi possível enviar agora. Tente novamente em instantes.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="diagnostico"
      data-testid="saas-form-section"
      className="relative py-24 sm:py-32 bg-[#1B1D1D] text-[#F5FAFB] overflow-hidden"
    >
      <div
        className="absolute -top-32 -left-20 h-[460px] w-[460px] rounded-full blur-3xl opacity-25"
        style={{
          background: "radial-gradient(closest-side, #3ED383, transparent 70%)",
        }}
      />
      <div
        className="absolute -bottom-32 -right-20 h-[400px] w-[400px] rounded-full blur-3xl opacity-20"
        style={{
          background: "radial-gradient(closest-side, #208BFF, transparent 70%)",
        }}
      />

      <div className="relative mx-auto max-w-7xl px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#3ED383]">
            Conversa, não checkout
          </span>
          <h2
            data-testid="saas-form-headline"
            className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[0.96] text-[#F5FAFB]"
          >
            Quero avaliar a RSM na minha organização.
          </h2>
          <p className="mt-6 text-base sm:text-lg leading-relaxed text-[#F5FAFB]/70 max-w-md">
            Vamos entender sua realidade e, se fizer sentido, desenhar um piloto
            juntos. Retorno em até 5 dias úteis, sem compromisso.
          </p>

          <div className="mt-10 space-y-4 text-sm text-[#F5FAFB]/75 max-w-sm">
            {[
              "Diagnóstico estruturado (situação, problema, implicação).",
              "Desenho sob medida para a sua comunidade.",
              "Sem auto-serviço — atendimento humano da RSM.",
            ].map((t, i) => (
              <div key={t} className="flex items-start gap-3">
                <CheckCircle2
                  size={18}
                  className="mt-0.5 text-[#3ED383] flex-shrink-0"
                />
                <span>{t}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <div
              data-testid="saas-form-success"
              className="rounded-2xl border border-[#3ED383]/30 bg-white/5 backdrop-blur p-10 text-center"
            >
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-[#3ED383]/20 text-[#3ED383]">
                <CheckCircle2 size={28} />
              </div>
              <h3 className="mt-6 font-display text-2xl font-semibold text-[#F5FAFB]">
                Interesse recebido.
              </h3>
              <p className="mt-3 text-[#F5FAFB]/70 max-w-md mx-auto">
                Em até 5 dias úteis alguém da RSM retorna para uma primeira
                conversa de diagnóstico sobre a sua organização.
              </p>
              <button
                data-testid="saas-form-reset"
                onClick={() => setDone(false)}
                className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-medium text-[#F5FAFB]/85 hover:border-[#3ED383] hover:text-[#3ED383] transition-colors"
              >
                Enviar outro contato
              </button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="saas-form"
              className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur p-6 sm:p-8"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Seu nome *">
                  <input
                    data-testid="saas-input-name"
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Nome completo"
                    className={inputCls}
                  />
                </Field>
                <Field label="Cargo / função">
                  <input
                    data-testid="saas-input-role"
                    value={form.role}
                    onChange={set("role")}
                    placeholder="Ex.: Diretor, gestor de RH"
                    className={inputCls}
                  />
                </Field>
                <Field label="Organização *">
                  <input
                    data-testid="saas-input-organization"
                    required
                    value={form.organization}
                    onChange={set("organization")}
                    placeholder="Nome da escola, entidade ou varejista"
                    className={inputCls}
                  />
                </Field>
                <Field label="Tipo de organização *">
                  <select
                    data-testid="saas-input-org-type"
                    required
                    value={form.org_type}
                    onChange={set("org_type")}
                    className={inputCls}
                  >
                    <option value="" disabled>
                      Selecione…
                    </option>
                    {ORG_TYPES.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="E-mail *">
                  <input
                    data-testid="saas-input-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    placeholder="voce@organizacao.com"
                    className={inputCls}
                  />
                </Field>
                <Field label="Telefone / WhatsApp">
                  <input
                    data-testid="saas-input-phone"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="(11) 90000-0000"
                    className={inputCls}
                  />
                </Field>
                <Field label="Tamanho da comunidade" className="sm:col-span-2">
                  <select
                    data-testid="saas-input-community-size"
                    value={form.community_size}
                    onChange={set("community_size")}
                    className={inputCls}
                  >
                    <option value="">Selecione (opcional)…</option>
                    {SIZES.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              <Field label="Principal desafio de engajamento" className="mt-4">
                <textarea
                  data-testid="saas-input-challenge"
                  value={form.main_challenge}
                  onChange={set("main_challenge")}
                  rows={3}
                  placeholder="O que hoje não funciona? Onde gostaria de mais participação?"
                  className={`${inputCls} resize-none`}
                />
              </Field>

              <Field label="Algo mais que ajude no diagnóstico?" className="mt-4">
                <textarea
                  data-testid="saas-input-message"
                  value={form.message}
                  onChange={set("message")}
                  rows={3}
                  placeholder="Contexto, prazos, recorrência…"
                  className={`${inputCls} resize-none`}
                />
              </Field>

              <button
                type="submit"
                disabled={loading}
                data-testid="saas-form-submit"
                className="mt-6 inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-[#208BFF] px-7 py-4 text-base font-semibold text-white shadow-sm transition-all duration-300 ease-out hover:bg-[#1B73D6] hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0 focus:outline-none focus:ring-2 focus:ring-[#208BFF] focus:ring-offset-2 focus:ring-offset-[#1B1D1D]"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Enviando…
                  </>
                ) : (
                  <>
                    Quero avaliar a RSM na minha organização
                    <ArrowRight size={18} />
                  </>
                )}
              </button>

              <p className="mt-4 text-xs text-[#F5FAFB]/55">
                Vamos entender sua realidade e, se fizer sentido, desenhar um
                piloto juntos. Seus dados não são compartilhados.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[11px] font-semibold tracking-[0.14em] uppercase text-[#F5FAFB]/55">
        {label}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

const inputCls =
  "w-full rounded-xl border border-white/12 bg-[#0e0f0f] px-4 py-3 text-sm text-[#F5FAFB] placeholder:text-[#F5FAFB]/30 focus:border-[#208BFF] focus:outline-none focus:ring-2 focus:ring-[#208BFF]/30 transition-all";
